import React, { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Paginacion from '../components/Paginacion';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import { formatFecha, formatFechaHora, hoyCaracas } from '../utils/format';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Actividad por usuario (#122): cuántos movimientos, facturas y pagos
// creó, editó, anuló o eliminó cada usuario en la empresa activa, y al
// hacer clic el detalle en palabras. Solo gerente y admin.
// ============================================================

const ACCIONES = [
  { key: 'CREAR', filtro: 'crear', label: 'Creó' },
  { key: 'ACTUALIZAR', filtro: 'editar', label: 'Editó' },
  { key: 'ANULAR', filtro: 'anular', label: 'Anuló' },
  { key: 'ELIMINAR', filtro: 'eliminar', label: 'Eliminó' }
];
const GRUPOS = [
  { key: 'movimientos', label: 'Movimientos' },
  { key: 'facturas', label: 'Facturas' },
  { key: 'pagos', label: 'Pagos de facturas' }
];
const ETIQUETA_ACCION = Object.fromEntries(ACCIONES.map((a) => [a.key, a.label]));
const COLOR_ACCION = {
  CREAR: 'bg-emerald-100 text-emerald-700',
  ACTUALIZAR: 'bg-blue-100 text-blue-700',
  ANULAR: 'bg-amber-100 text-amber-800',
  ELIMINAR: 'bg-red-100 text-red-700'
};

function haceDias(n) {
  const d = new Date(`${hoyCaracas()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

// "3 mov. · 1 fact." de una celda acción × grupos.
function textoCelda(porGrupo, grupo) {
  if (grupo) return porGrupo[grupo] || '';
  const corto = { movimientos: 'mov.', facturas: 'fact.', pagos: 'pagos' };
  return GRUPOS.filter((g) => porGrupo[g.key]).map((g) => `${porGrupo[g.key]} ${corto[g.key]}`).join(' · ');
}

export default function ReporteActividadUsuario() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [desde, setDesde] = useState(haceDias(30));
  const [hasta, setHasta] = useState(hoyCaracas());
  const [usuarioId, setUsuarioId] = useState('');
  const [accion, setAccion] = useState('');
  const [grupo, setGrupo] = useState('');

  const [resumen, setResumen] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  // Detalle de un usuario (o de todos con el filtro de usuario)
  const [detalleDe, setDetalleDe] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [pagina, setPagina] = useState(1);

  const filtros = {
    desde: desde || undefined, hasta: hasta || undefined,
    accion: accion || undefined, grupo: grupo || undefined
  };

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    setError('');
    api.get('/auditoria/actividad', { params: { ...filtros, usuario_id: usuarioId || undefined } })
      .then(({ data }) => {
        setResumen(data);
        // La lista del filtro acumula los usuarios vistos, para no perder
        // al elegido cuando el filtro deja solo a uno.
        setUsuarios((prev) => {
          const m = new Map(prev.map((u) => [u.usuario_id, u]));
          data.forEach((u) => m.set(u.usuario_id, { usuario_id: u.usuario_id, nombre: u.nombre }));
          return [...m.values()].sort((a, b) => String(a.nombre).localeCompare(String(b.nombre)));
        });
      })
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar la actividad'))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva, desde, hasta, usuarioId, accion, grupo]);

  useEffect(() => { setDetalleDe(null); setUsuarios([]); setUsuarioId(''); }, [empresaActiva]);
  useEffect(() => { setPagina(1); }, [detalleDe, desde, hasta, accion, grupo]);

  useEffect(() => {
    if (!detalleDe) { setDetalle(null); return; }
    api.get('/auditoria/actividad/detalle', { params: { ...filtros, usuario_id: detalleDe.usuario_id, pagina } })
      .then(({ data }) => setDetalle(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el detalle'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleDe, pagina, desde, hasta, accion, grupo]);

  const acciones = accion ? ACCIONES.filter((a) => a.filtro === accion) : ACCIONES;

  const textoFiltros = [
    desde || hasta ? `Del ${formatFecha(desde) || '…'} al ${formatFecha(hasta) || '…'}` : 'Todas las fechas',
    accion && ACCIONES.find((a) => a.filtro === accion)?.label,
    grupo && GRUPOS.find((g) => g.key === grupo)?.label
  ].filter(Boolean).join(' · ');

  // Excel: lo que se ve. En el detalle, todas sus páginas.
  const contenidoExcel = async () => {
    if (detalleDe) {
      const filas = [];
      for (let p = 1; p <= 200; p += 1) {
        const { data } = await api.get('/auditoria/actividad/detalle', { params: { ...filtros, usuario_id: detalleDe.usuario_id, pagina: p } });
        data.datos.forEach((d) => filas.push([
          formatFechaHora(d.creado_en), ETIQUETA_ACCION[d.accion] || d.accion,
          d.descripcion || `${ETIQUETA_ACCION[d.accion] || d.accion} ${d.entidad} #${d.entidad_id}`
        ]));
        if (p >= data.totalPaginas) break;
      }
      return {
        titulo: `Actividad de ${detalleDe.nombre}`,
        detalle: textoFiltros,
        hojas: [{
          nombre: 'Detalle',
          columnas: [{ titulo: 'Fecha y hora', tipo: 'texto' }, { titulo: 'Acción', tipo: 'texto' }, { titulo: 'Qué hizo', tipo: 'texto' }],
          filas
        }]
      };
    }
    return {
      titulo: 'Actividad por usuario',
      detalle: textoFiltros,
      hojas: [{
        nombre: 'Resumen',
        columnas: [
          { titulo: 'Usuario', tipo: 'texto' }, { titulo: 'Correo', tipo: 'texto' },
          ...acciones.map((a) => ({ titulo: a.label, tipo: 'texto' })),
          { titulo: 'Total', tipo: 'entero' }, { titulo: 'Última vez', tipo: 'texto' }
        ],
        filas: resumen.map((u) => [
          u.nombre, u.email, ...acciones.map((a) => textoCelda(u.acciones[a.key], grupo) || ''), u.total, formatFechaHora(u.ultima)
        ])
      }]
    };
  };

  return (
    <div className="space-y-6">
      <div className="print:hidden">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-2xl font-bold text-slate-800">
            Actividad por usuario
            {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
            <BotonAyuda />
          </h1>
          <Exportar excel={contenidoExcel} disabled={detalleDe ? !detalle : cargando} />
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Cuántos movimientos, facturas y pagos creó, editó, anuló o eliminó cada usuario. Haz clic en un usuario para ver qué hizo.
        </p>
      </div>

      <EncabezadoImpresion titulo={detalleDe ? `Actividad de ${detalleDe.nombre}` : 'Actividad por usuario'} detalle={textoFiltros} />

      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3 text-sm print:hidden">
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-md px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-md px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">Usuario</span>
          <select value={usuarioId} onChange={(e) => { setUsuarioId(e.target.value); setDetalleDe(null); }} className="border rounded-md px-3 py-2">
            <option value="">Todos</option>
            {usuarios.map((u) => <option key={u.usuario_id} value={u.usuario_id}>{u.nombre}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">Acción</span>
          <select value={accion} onChange={(e) => setAccion(e.target.value)} className="border rounded-md px-3 py-2">
            <option value="">Todas</option>
            {ACCIONES.map((a) => <option key={a.filtro} value={a.filtro}>{a.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">Sobre</span>
          <select value={grupo} onChange={(e) => setGrupo(e.target.value)} className="border rounded-md px-3 py-2">
            <option value="">Todo</option>
            {GRUPOS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
          </select>
        </label>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {detalleDe ? (
        <div className="space-y-3">
          <button onClick={() => setDetalleDe(null)} className="print:hidden inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary-600">
            <ChevronLeft size={16} /> Volver al resumen
          </button>
          <h2 className="text-lg font-semibold text-slate-700 print:hidden">{detalleDe.nombre}</h2>
          {!detalle ? (
            <p className="text-slate-400 text-sm">Cargando…</p>
          ) : detalle.datos.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">Sin actividad con estos filtros.</div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3 whitespace-nowrap">Fecha y hora</th>
                    <th className="p-3">Acción</th>
                    <th className="p-3">Qué hizo</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.datos.map((d) => (
                    <tr key={d.id} className="border-t align-top">
                      <td className="p-3 whitespace-nowrap text-slate-500">{formatFechaHora(d.creado_en)}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${COLOR_ACCION[d.accion] || 'bg-slate-100 text-slate-600'}`}>
                          {ETIQUETA_ACCION[d.accion] || d.accion}
                        </span>
                      </td>
                      <td className="p-3">{d.descripcion || `${ETIQUETA_ACCION[d.accion] || d.accion} ${d.entidad} #${d.entidad_id}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Paginacion pagina={detalle.pagina} totalPaginas={detalle.totalPaginas} total={detalle.total} onCambiarPagina={setPagina} />
            </div>
          )}
        </div>
      ) : cargando ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : resumen.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-slate-400">Nadie registró actividad con estos filtros.</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3">Usuario</th>
                {acciones.map((a) => <th key={a.key} className="p-3">{a.label}</th>)}
                <th className="p-3 text-right">Total</th>
                <th className="p-3 whitespace-nowrap">Última vez</th>
              </tr>
            </thead>
            <tbody>
              {resumen.map((u) => (
                <tr key={u.usuario_id} {...filaClickeable(() => setDetalleDe(u))} className={`border-t cursor-pointer hover:bg-slate-50 ${FOCO_FILA}`}>
                  <td className="p-3">
                    <div className="font-medium text-primary-700">{u.nombre}</div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </td>
                  {acciones.map((a) => (
                    <td key={a.key} className={`p-3 whitespace-nowrap ${a.key === 'ELIMINAR' && Object.keys(u.acciones[a.key]).length ? 'text-red-600' : 'text-slate-600'}`}>
                      {textoCelda(u.acciones[a.key], grupo) || <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                  <td className="p-3 text-right font-semibold">{u.total}</td>
                  <td className="p-3 whitespace-nowrap text-slate-500">{formatFechaHora(u.ultima)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
